import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AdminSubNav, SUBNAV_ANALYTICS } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (c) => `${c < 0 ? '−' : ''}$${(Math.abs(c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const th = 'text-left py-3 px-4 text-[9px] tracking-[0.14em] uppercase text-[#7a7c6d] font-bold';
const td = 'py-3 px-4 text-sm text-[#20211d]';

const Table = ({ cols, rows, empty, testid }) => (
  <div className="rounded-[10px] border border-[#e3e0d6] bg-[#fbfaf7] overflow-x-auto" data-testid={testid}>
    {rows.length === 0 ? <p className="p-6 text-sm text-[#77776e]">{empty}</p> : (
      <table className="w-full min-w-[560px]"><thead><tr className="border-b border-[#e3e0d6]">{cols.map((c) => <th key={c[0]} className={th}>{c[1]}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-[#ecebe4] last:border-0">{cols.map((c) => <td key={c[0]} className={td}>{c[2] ? c[2](r) : r[c[0]]}</td>)}</tr>)}</tbody></table>
    )}
  </div>
);

const VIEWS = {
  expenses: (d) => <Table testid="insights-expenses" empty="No paid Guru payouts or supply receipts recorded yet."
    rows={d.months.filter((m) => m.guru_pay_cents || m.supplies_cents)}
    cols={[['month', 'Month', (r) => monthLabel(r.month)], ['guru', 'Guru pay (paid payouts)', (r) => money(r.guru_pay_cents)], ['sup', 'Supplies (receipts)', (r) => money(r.supplies_cents)], ['tot', 'Total spending', (r) => money(r.guru_pay_cents + r.supplies_cents)]]} />,
  'cash-flow': (d) => <Table testid="insights-cash-flow" empty="No money in or out recorded yet."
    rows={d.months} cols={[['month', 'Month', (r) => monthLabel(r.month)], ['in', 'Cash in', (r) => money(r.cash_in_cents)], ['out', 'Cash out', (r) => money(r.guru_pay_cents + r.supplies_cents)], ['net', 'Net movement', (r) => <b className={r.net_cents < 0 ? 'text-[#a4471b]' : ''}>{money(r.net_cents)}</b>]]} />,
  services: (d) => <Table testid="insights-services" empty="No completed visits yet."
    rows={d.services} cols={[['service', 'Service'], ['visits', 'Completed visits'], ['q', 'Quoted value', (r) => money(r.quoted_cents)], ['avg', 'Avg time on site', (r) => (r.avg_minutes != null ? `${Math.floor(r.avg_minutes / 60)}h ${r.avg_minutes % 60}m` : '—')]]} />,
  'data-checks': (d) => (
    <ul className="rounded-[10px] border border-[#e3e0d6] bg-[#fbfaf7] divide-y divide-[#ecebe4]" data-testid="insights-data-checks">
      {d.checks.map((c) => (
        <li key={c.key} className="flex items-center gap-4 px-5 py-4" data-testid={`data-check-${c.key}`}>
          <span className={`w-10 text-2xl ${c.count ? 'text-[#a4471b]' : 'text-[#55704a]'}`}>{c.count}</span>
          <span className="flex-1 text-sm text-[#20211d]">{c.label}</span>
          {c.count > 0 ? <Link to={c.href} className="text-xs font-semibold text-[#b75b28]">Review ↗</Link> : <span className="text-xs text-[#55704a]">All clear</span>}
        </li>
      ))}
    </ul>
  ),
};

export default function AdminAnalyticsInsightsPage({ view }) {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { axios.get(`${API_URL}/api/admin/analytics/insights`, { headers, params: { months: 12 } }).then((r) => setD(r.data)).catch(() => setErr('Analytics could not be loaded.')); }, [headers]);
  return (
    <div data-testid={`analytics-${view}-page`}>
      <AdminSubNav section="Analytics" items={SUBNAV_ANALYTICS} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-3">
        {err ? <p className="text-sm text-[#a4471b]">{err}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin text-[#f36a19]" /> : <>{VIEWS[view](d)}{view !== 'data-checks' && <p className="text-[11px] text-[#77776e]">{d.note}</p>}</>}
      </main>
    </div>
  );
}
